import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BriefcaseBusiness, CalendarDays, Crown, Download, Loader2, Search, TrendingUp } from "lucide-react";
import { api, fmt, apiError } from "@/lib/api";

const TABS = [
  { id: "sales", label: "Sales" },
  { id: "purchases", label: "Purchases" },
  { id: "inventory", label: "Inventory" },
  { id: "receivables", label: "Receivables" },
  { id: "payables", label: "Supplier Payables" },
  { id: "returns", label: "Returns & Refunds" },
  { id: "delivery", label: "Delivery Costs" },
  { id: "expenses", label: "Expenses" },
  { id: "profit", label: "Profit & Margin" },
];

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "year", label: "This Year" },
  { id: "all", label: "All Time" },
  { id: "custom", label: "Custom" },
];

const STATUS_OPTIONS = {
  sales: [
    { param: "pay_status", label: "Payment", values: ["paid", "cod_pending", "credit_pending", "pending", "failed"] },
    { param: "method", label: "Method", values: ["cod", "card", "upi", "netbanking", "packo_credit"] },
    { param: "status", label: "Order", values: ["confirmed", "shipped", "out_for_delivery", "delivered", "cancelled"] },
  ],
  purchases: [{ param: "status", label: "Status", values: ["ordered", "received"] }],
  receivables: [{ param: "kind_filter", label: "Type", values: ["order", "credit"] }],
  payables: [{ param: "status", label: "Status", values: ["ordered", "received"] }],
  returns: [{ param: "status", label: "Status", values: ["requested", "approved", "rejected", "received", "refunded"] }],
  delivery: [{ param: "method", label: "Partner", values: ["self_fleet", "porter", "dunzo", "delhivery", "bluedart", "dtdc", "other"] }],
  expenses: [{ param: "status", label: "Category", values: ["Warehouse", "Packaging", "Salary", "Utilities", "Fuel", "Marketing", "Software", "Repairs", "Office", "Other"] }],
};

const MONEY_KEYS = new Set(["subtotal", "discount", "delivery", "tax", "total", "unit_cost", "value_at_cost",
  "unit_price", "sellable_value", "amount", "outstanding", "amount_paid", "refund_amount",
  "net_sales", "estimated_cogs", "gross_profit", "collected", "delivery_cost", "profit_after_delivery"]);
const PCT_KEYS = new Set(["margin_pct", "cost_coverage_pct"]);
const NUMERIC_TOTAL_KEYS = new Set(["subtotal", "discount", "delivery", "tax", "total", "items", "stock",
  "reserved", "value_at_cost", "sellable_value", "amount", "outstanding", "qty", "refund_amount",
  "net_sales", "estimated_cogs", "gross_profit", "collected", "delivery_cost", "profit_after_delivery"]);

function isoDay(d) { return d.toISOString().slice(0, 10); }

function presetRange(id) {
  const now = new Date();
  const today = isoDay(now);
  if (id === "today") return { start: today, end: today };
  if (id === "week") {
    const mon = new Date(now);
    mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return { start: isoDay(mon), end: today };
  }
  if (id === "month") return { start: `${today.slice(0, 8)}01`, end: today };
  if (id === "year") return { start: `${today.slice(0, 4)}-01-01`, end: today };
  return { start: "", end: "" };
}

function Cell({ k, v }) {
  if (v === null || v === undefined || v === "") return <span className="text-neutral-600">—</span>;
  if (MONEY_KEYS.has(k) && typeof v === "number") return <span className="tabular-nums">{fmt(v)}</span>;
  if (PCT_KEYS.has(k) && typeof v === "number") return <span className="tabular-nums">{v}%</span>;
  return <span>{String(v)}</span>;
}

export default function AdminBusiness() {
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const range = preset === "custom" ? custom : presetRange(preset);

  const [summary, setSummary] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [tab, setTab] = useState("sales");
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => ({
    ...(range.start ? { start: range.start } : {}),
    ...(range.end ? { end: range.end } : {}),
  }), [range.start, range.end]);

  useEffect(() => {
    setSummary(null);
    api.get("/admin/business/summary", { params })
      .then(({ data }) => setSummary(data))
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error(apiError(e, "Couldn't load the business summary"));
      });
  }, [params]);

  const loadReport = useCallback(() => {
    setLoadingReport(true);
    api.get(`/admin/business/reports/${tab}`, { params: { ...params, q, ...filters } })
      .then(({ data }) => setReport(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load the report")))
      .finally(() => setLoadingReport(false));
  }, [tab, params, q, filters]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/business/reports/${tab}/export`, {
        params: { ...params, q, ...filters }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `paqvero-${tab}-${range.start || "all"}-${range.end || "time"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(apiError(e, "Couldn't export the CSV"));
    } finally {
      setExporting(false);
    }
  };

  if (forbidden) {
    return (
      <div data-testid="business-forbidden" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center">
        <Crown className="mx-auto mb-3 h-8 w-8 text-amber-500/60" />
        <p className="text-sm font-semibold text-white">Owner access only</p>
        <p className="mt-1 text-xs text-neutral-500">Business Operations is restricted to the account owner.</p>
      </div>
    );
  }

  const kpis = summary ? [
    { label: "Total Sales", value: fmt(summary.sales.total_sales), sub: `${summary.sales.orders} orders${summary.sales.cancelled_orders ? ` · ${summary.sales.cancelled_orders} cancelled excluded` : ""}`, testid: "kpi-total-sales" },
    { label: "Net Sales", value: fmt(summary.sales.net_sales), sub: `after ${fmt(summary.sales.discounts)} discounts · ${fmt(summary.sales.refunds)} refunds`, testid: "kpi-net-sales" },
    { label: "Paid", value: fmt(summary.payments.paid_amount), sub: `${summary.payments.paid_orders} orders`, testid: "kpi-paid" },
    { label: "Unpaid", value: fmt(summary.payments.unpaid_amount), sub: `${summary.payments.unpaid_orders} orders`, testid: "kpi-unpaid" },
    { label: "COD", value: fmt(summary.payments.cod_amount), sub: `${summary.payments.cod_orders} orders · ${fmt(summary.payments.cod_collected)} collected`, testid: "kpi-cod" },
    { label: "Online (Razorpay)", value: fmt(summary.payments.online_amount), sub: `${summary.payments.online_orders} orders`, testid: "kpi-online" },
    { label: "PAQVERO Credit Sales", value: fmt(summary.payments.credit_amount), sub: `${summary.payments.credit_orders} orders`, testid: "kpi-credit" },
    { label: "Receivables", value: fmt(summary.receivables.total), sub: `${fmt(summary.receivables.unpaid_orders_amount)} orders + ${fmt(summary.receivables.credit_outstanding)} credit`, testid: "kpi-receivables" },
    { label: "Purchase Cost", value: fmt(summary.purchases.received_cost), sub: `${summary.purchases.count} POs · ${fmt(summary.purchases.outstanding)} outstanding to suppliers`, testid: "kpi-purchases" },
    { label: "Inventory Value", value: fmt(summary.inventory.value_at_cost), sub: summary.inventory.cost_coverage_pct !== null ? `${summary.inventory.cost_coverage_pct}% of units have a cost basis` : "no cost basis yet", testid: "kpi-inventory" },
    { label: "Refunds", value: fmt(summary.returns.refunded_amount), sub: `${summary.returns.count} returns · ${summary.returns.open} open`, testid: "kpi-refunds" },
    { label: "Est. Gross Profit", value: fmt(summary.margin.estimated_gross_profit), sub: summary.margin.gross_margin_pct !== null ? `${summary.margin.gross_margin_pct}% margin · ${summary.margin.cost_coverage_pct}% COGS coverage` : "no sales in range", testid: "kpi-margin" },
    { label: "Profit After Delivery", value: fmt(summary.margin.profit_after_delivery), sub: summary.margin.delivery_cost_coverage_pct !== null ? `delivery cost ${fmt(summary.margin.delivery_cost)} · recorded for ${summary.margin.delivery_cost_coverage_pct}% of orders` : "no orders in range", testid: "kpi-profit-after-delivery" },
    { label: "Total Expenses", value: fmt(summary.expenses.total), sub: `${summary.expenses.count} recorded — exact, not estimated`, testid: "kpi-expenses" },
    { label: "Net Profit (after expenses)", value: fmt(summary.margin.net_profit_after_expenses), sub: summary.margin.net_margin_pct !== null ? `${summary.margin.net_margin_pct}% net margin · still excludes unrecorded costs` : "no sales in range", testid: "kpi-net-profit" },
    { label: "Delivery Revenue / Cost", value: `${fmt(summary.sales.delivery_revenue)} / ${fmt(summary.sales.delivery_cost)}`, sub: "charged to customers vs recorded courier cost", testid: "kpi-delivery" },
  ] : [];

  return (
    <div data-testid="business-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-white">
            <BriefcaseBusiness className="h-6 w-6 text-amber-400" /> Business Operations
          </h1>
          <p className="mt-1 text-sm text-neutral-500">Owner-only accounting view. All figures come from live orders, invoices, purchases, returns and the credit ledger. GST reporting is off while PAQVERO is pre-GST.</p>
        </div>
      </div>

      <div data-testid="business-date-filters" className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/[0.07] bg-white/[0.02] p-3">
        <CalendarDays className="ml-1 h-4 w-4 text-neutral-500" />
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => setPreset(p.id)} data-testid={`preset-${p.id}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${preset === p.id ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "text-neutral-400 border border-white/10 hover:bg-white/[0.05]"}`}>
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <span className="flex items-center gap-2">
            <input type="date" value={custom.start} max={custom.end || undefined} data-testid="custom-start"
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
              className="h-8 rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
            <span className="text-xs text-neutral-500">to</span>
            <input type="date" value={custom.end} min={custom.start || undefined} data-testid="custom-end"
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
              className="h-8 rounded-lg border border-white/10 bg-black/40 px-2 text-xs text-white focus:border-amber-500/60 focus:outline-none" />
          </span>
        )}
        {range.start && <span className="ml-auto text-[11px] text-neutral-500">{range.start} → {range.end}</span>}
      </div>

      {!summary ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : (
        <div data-testid="business-kpis" className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
          {kpis.map((k) => (
            <div key={k.label} data-testid={k.testid} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">{k.label}</p>
              <p className="mt-1 text-xl font-bold text-white">{k.value}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-neutral-500">{k.sub}</p>
            </div>
          ))}
        </div>
      )}

      {summary && (summary.inventory.cost_coverage_pct !== null && summary.inventory.cost_coverage_pct < 100) && (
        <p data-testid="business-coverage-note" className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-2.5 text-xs text-amber-200/80">
          Cost basis is missing for some SKUs — inventory value and margin only cover items purchased through Admin → Purchases ({summary.inventory.cost_coverage_pct}% of stock). Record supplier purchases to make these figures complete.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-b border-white/[0.07] pb-px" data-testid="business-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); setQ(""); setFilters({}); setReport(null); }} data-testid={`report-tab-${t.id}`}
            className={`flex items-center gap-1.5 rounded-t-xl px-4 py-2.5 text-xs font-bold transition-colors ${tab === t.id ? "bg-white/[0.05] text-amber-300 border-b-2 border-amber-500" : "text-neutral-500 hover:text-neutral-300"}`}>
            {t.id === "profit" && <TrendingUp className="h-3.5 w-3.5" />}
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-600" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="report-search"
            className="h-10 w-64 rounded-xl border border-white/10 bg-black/40 pl-10 pr-3 text-xs text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none" />
        </div>
        {(STATUS_OPTIONS[tab] || []).map((f) => (
          <select key={f.param} value={filters[f.param] || ""} data-testid={`filter-${f.param}`}
            onChange={(e) => setFilters((s) => ({ ...s, [f.param]: e.target.value }))}
            className="h-10 rounded-xl border border-white/10 bg-black/40 px-3 text-xs text-white focus:border-amber-500/60 focus:outline-none">
            <option value="">{f.label}: all</option>
            {f.values.map((v) => <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
          </select>
        ))}
        <button onClick={exportCsv} disabled={exporting || !report} data-testid="export-csv-btn"
          className="ml-auto inline-flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:opacity-40">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export CSV
        </button>
      </div>

      {tab === "payables" && (
        <p className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-4 py-2.5 text-xs text-neutral-400">
          Paid and outstanding amounts come from payments recorded in Admin → Purchases (Record payment).
        </p>
      )}

      {tab === "profit" && summary && (
        <p data-testid="profit-coverage-note" className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-2.5 text-xs text-amber-200/80">
          Profit here is always an estimate, never exact: COGS covers {summary.margin.cost_coverage_pct ?? 0}% of units sold and delivery cost is recorded for {summary.margin.delivery_cost_coverage_pct ?? 0}% of orders in range — missing costs count as ₹0. Recorded expenses ({fmt(summary.expenses.total)}) are exact and deducted in full for the Net Profit card.
        </p>
      )}

      {tab === "expenses" && summary && summary.expenses.count > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="business-expense-breakdown">
          {Object.entries(summary.expenses.by_category).map(([c, v]) => (
            <span key={c} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-semibold text-neutral-300">
              {c}: {fmt(v)}
            </span>
          ))}
        </div>
      )}

      {loadingReport || !report ? (
        <div className="flex justify-center py-14"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>
      ) : report.rows.length === 0 ? (
        <div data-testid="report-empty" className="rounded-2xl border border-white/[0.07] bg-white/[0.02] py-16 text-center text-sm text-neutral-500">
          Nothing matches — try widening the date range or clearing filters.
        </div>
      ) : (
        <div data-testid="report-table" className="overflow-x-auto rounded-2xl border border-white/[0.07]">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-white/[0.07] bg-white/[0.03] text-[10px] uppercase tracking-wider text-neutral-500">
                {report.columns.map((c) => <th key={c.key} className="whitespace-nowrap px-3 py-3">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r, i) => (
                <tr key={i} data-testid={`report-row-${i}`} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                  {report.columns.map((c) => (
                    <td key={c.key} className="whitespace-nowrap px-3 py-2.5 text-neutral-300"><Cell k={c.key} v={r[c.key]} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr data-testid="report-totals" className="bg-white/[0.03] text-[11px] font-bold text-white">
                {report.columns.map((c, i) => (
                  <td key={c.key} className="whitespace-nowrap px-3 py-3">
                    {i === 0 ? `Totals (${report.count})` : NUMERIC_TOTAL_KEYS.has(c.key) && report.totals[c.key] !== undefined
                      ? (MONEY_KEYS.has(c.key) ? fmt(report.totals[c.key]) : Number(report.totals[c.key]).toLocaleString("en-IN")) : ""}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
